import {
  BotIcon,
  CheckIcon,
  EyeOffIcon,
  PencilIcon,
  RefreshCwIcon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useApproveMessage } from '../hooks/useApprovals';
import { RejectReasonPicker } from './RejectReasonPicker';
import type { PendingApproval } from './SupportBotButton';

export const PendingApprovalBubble = ({
  approval,
  onDismiss,
  onRetryResult,
}: {
  approval: PendingApproval;
  onDismiss: () => void;
  onRetryResult?: (newApproval: PendingApproval) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(approval.message);
  const [saveAsExample, setSaveAsExample] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryInstructions, setRetryInstructions] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const { approveMutation, rejectMutation, editMutation, retryMutation } =
    useApproveMessage();

  const isPending =
    approveMutation.isPending ||
    rejectMutation.isPending ||
    editMutation.isPending ||
    retryMutation.isPending;

  const handleApprove = () => {
    approveMutation.mutate(
      {
        approvalId: approval.approval_id,
        approvedActionIds: approval.actions.map((a) => a.action_id),
        saveAsExample,
      },
      { onSuccess: onDismiss }
    );
  };

  const handleReject = () => {
    setShowRejectModal(true);
  };

  const handleRetry = () => {
    if (!retryInstructions.trim()) return;
    retryMutation.mutate(
      {
        approvalId: approval.approval_id,
        additionalInstructions: retryInstructions,
      },
      {
        onSuccess: (data) => {
          setShowRetryModal(false);
          setRetryInstructions('');
          if (data.approval_id && onRetryResult) {
            onRetryResult({
              approval_id: data.approval_id,
              message: data.message ?? '',
              actions: data.actions ?? [],
              task_id: data.task_id ?? 'unknown',
              internal: data.internal,
            });
          } else {
            onDismiss();
          }
        },
      }
    );
  };

  const handleEditAndApprove = () => {
    editMutation.mutate(
      {
        approvalId: approval.approval_id,
        editedMessage,
        approvedActionIds: approval.actions.map((a) => a.action_id),
        saveAsExample,
      },
      { onSuccess: onDismiss }
    );
  };

  return (
    <div className="mb-4 flex justify-start">
      <div
        className={`flex flex-col gap-3 rounded-2xl border p-4 ${
          isEditing ? 'w-full max-w-[80rem]' : 'max-w-[50rem]'
        } ${
          approval.internal
            ? 'border-amber-200 bg-amber-50'
            : 'border-blue-200 bg-blue-50'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-2">
          <BotIcon
            className={`h-5 w-5 ${
              approval.internal ? 'text-amber-600' : 'text-blue-600'
            }`}
          />
          <span
            className={`text-[1.3rem] font-medium ${
              approval.internal ? 'text-amber-600' : 'text-blue-600'
            }`}
          >
            {approval.internal ? 'Internal Action' : 'AI Suggestion'}
          </span>
          <span
            className={`rounded-full px-2 py-0.5 text-[1.1rem] ${
              approval.internal
                ? 'bg-amber-100 text-amber-500'
                : 'bg-blue-100 text-blue-500'
            }`}
          >
            {approval.task_id}
          </span>
          {approval.internal && (
            <span className="flex items-center gap-1 text-[1.1rem] text-amber-500">
              <EyeOffIcon className="h-3.5 w-3.5" />
              Not visible to customer
            </span>
          )}
        </div>

        {/* Message */}
        {isEditing ? (
          <textarea
            className="min-h-[16rem] w-full rounded-lg border border-blue-300 bg-white p-3 text-[1.4rem] leading-relaxed text-zinc-800 focus:border-blue-400 focus:outline-none"
            value={editedMessage}
            onChange={(e) => setEditedMessage(e.target.value)}
          />
        ) : (
          <p className="whitespace-pre-wrap text-[1.4rem] leading-relaxed text-zinc-800">
            {approval.message}
          </p>
        )}

        {/* Actions */}
        {approval.actions.length > 0 && (
          <div className="flex flex-col gap-1 border-t border-blue-200 pt-2">
            <span className="text-[1.2rem] font-medium text-zinc-500">
              Proposed Actions
            </span>
            {approval.actions.map((action) => (
              <div
                key={action.action_id}
                className="flex items-center gap-2 text-[1.2rem] text-zinc-600"
              >
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[1.1rem]">
                  {action.type}
                </span>
                <span>{action.description}</span>
              </div>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 border-t border-blue-200 pt-3">
          {/* Save as example checkbox */}
          <label className="mr-auto flex cursor-pointer items-center gap-1.5 text-[1.2rem] text-zinc-500">
            <input
              type="checkbox"
              checked={saveAsExample}
              onChange={(e) => setSaveAsExample(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300"
            />
            Save as example
          </label>
          {isEditing ? (
            <>
              <button
                onClick={handleEditAndApprove}
                disabled={isPending || !editedMessage.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-[1.2rem] font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                Send Edited
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedMessage(approval.message);
                }}
                className="flex items-center gap-1.5 rounded-lg bg-zinc-200 px-3 py-1.5 text-[1.2rem] font-medium text-zinc-700 transition-colors hover:bg-zinc-300"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleApprove}
                disabled={isPending}
                className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-[1.2rem] font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-50"
              >
                <CheckIcon className="h-4 w-4" />
                {approval.internal ? 'Approve' : 'Approve & Send'}
              </button>
              <button
                onClick={() => setIsEditing(true)}
                disabled={isPending}
                className="flex items-center gap-1.5 rounded-lg bg-zinc-200 px-3 py-1.5 text-[1.2rem] font-medium text-zinc-700 transition-colors hover:bg-zinc-300 disabled:opacity-50"
              >
                <PencilIcon className="h-4 w-4" />
                Edit
              </button>
              <button
                onClick={() => setShowRetryModal(true)}
                disabled={isPending}
                className="flex items-center gap-1.5 rounded-lg bg-amber-100 px-3 py-1.5 text-[1.2rem] font-medium text-amber-700 transition-colors hover:bg-amber-200 disabled:opacity-50"
              >
                <RefreshCwIcon className="h-4 w-4" />
                Retry
              </button>
              <button
                onClick={handleReject}
                disabled={isPending}
                className="flex items-center gap-1.5 rounded-lg bg-red-100 px-3 py-1.5 text-[1.2rem] font-medium text-red-700 transition-colors hover:bg-red-200 disabled:opacity-50"
              >
                <XIcon className="h-4 w-4" />
                Reject
              </button>
            </>
          )}
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <RejectReasonPicker
            isPending={rejectMutation.isPending}
            onCancel={() => setShowRejectModal(false)}
            onConfirm={({ reasonTags, reason }) => {
              rejectMutation.mutate(
                {
                  approvalId: approval.approval_id,
                  reason,
                  reasonTags,
                },
                {
                  onSuccess: () => {
                    setShowRejectModal(false);
                    onDismiss();
                  },
                }
              );
            }}
          />
        )}

        {/* Retry Modal */}
        {showRetryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="mb-2 text-[1.5rem] font-semibold text-zinc-800">
                Retry with Instructions
              </h3>
              <p className="mb-4 text-[1.3rem] text-zinc-500">
                What should the AI do differently?
              </p>
              <textarea
                className="mb-4 min-h-[10rem] w-full rounded-lg border border-zinc-300 bg-white p-3 text-[1.4rem] leading-relaxed text-zinc-800 focus:border-blue-400 focus:outline-none"
                value={retryInstructions}
                onChange={(e) => setRetryInstructions(e.target.value)}
                placeholder="e.g. Be more empathetic, offer a partial refund, ask for photos first..."
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowRetryModal(false);
                    setRetryInstructions('');
                  }}
                  className="rounded-lg bg-zinc-200 px-4 py-2 text-[1.2rem] font-medium text-zinc-700 transition-colors hover:bg-zinc-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRetry}
                  disabled={
                    retryMutation.isPending || !retryInstructions.trim()
                  }
                  className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-[1.2rem] font-medium text-white transition-colors hover:bg-amber-600 disabled:opacity-50"
                >
                  {retryMutation.isPending ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <RefreshCwIcon className="h-4 w-4" />
                  )}
                  Retry
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
